import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { Contract } from 'src/app/compliance/models/contract';
import {
    loadContractsForCandidate,
    loadContractsForCandidateFailure,
    loadContractsForCandidateSuccess
} from 'src/app/compliance/store/actions/contract.actions';
import {
    contractSelected,
    searchDocumentTypesByName
} from 'src/app/compliance/store/actions/document-reviewer.actions';
import { selectComplianceCandidateId } from 'src/app/compliance/store/selectors/compliance.selectors';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ComplianceReportClient } from '../../api/compliance-report-client.service';
import { updateCurrentComplianceStatus } from '../actions/compliance-report-new.actions';
import {
    complianceStateChanged,
    complianceStateChangeSuccess,
    complianceStatusChanged,
    complianceStatusChangeSuccess,
    contractChanged
} from '../actions/contract.actions';
import { DocumentUploadToolClient } from '../../api/document-upload-tool-client.service';
import { ContractsClient } from '../../api/contracts-client.service';

@Injectable()
export class ContractEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store$: Store,
        private readonly _contractsClient: ContractsClient,
        private readonly _documentUploadService: DocumentUploadToolClient,
        private readonly complianceReportClient: ComplianceReportClient
    ) {}

    updateComplianceStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(complianceStatusChanged),
            switchMap((action) =>
                this._contractsClient
                    .updateContract({
                        contractIds: [action.contractId],
                        complianceState: {
                            statusId: action.statusId,
                            isSystemGenerated: false
                        }
                    } as any)
                    .pipe(
                        mergeMap((_) => [
                            complianceStatusChangeSuccess(),
                            contractChanged({ id: action.contractId }),
                            updateCurrentComplianceStatus({ complianceStatus: action.statusId })
                        ])
                    )
            )
        );
    });

    updateComplianceState$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(complianceStateChanged),
            switchMap((action) =>
                this.complianceReportClient
                    .updateComplianceReportStatus(action.contractId, action.state)
                    .pipe(mergeMap((_) => [complianceStateChangeSuccess(), contractChanged({ id: action.contractId })]))
            )
        );
    });

    getContractsForCandidate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadContractsForCandidate),
            concatLatestFrom(() => this._store$.select(selectComplianceCandidateId)),
            switchMap(([action, candidateId]) => {
                return this._documentUploadService.listContractHistoryForCandidate(candidateId).pipe(
                    map((result: Contract[]) => {
                        result = this._addMasterFileToContracts(result);

                        if (action?.contractId) {
                            const contract = result.find((contract) => contract.id === action.contractId);
                            this._store$.dispatch(contractSelected({ contract: contract }));
                            this._store$.dispatch(searchDocumentTypesByName({ searchText: '' }));
                        }

                        return loadContractsForCandidateSuccess({ contracts: result });
                    }),
                    catchError((result) => of(loadContractsForCandidateFailure({ error: result })))
                );
            })
        );
    });

    showComplianceStatusSuccessToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(complianceStatusChangeSuccess),
            map(() => showToasterMessage({ message: 'Successfully updated compliance status' }))
        );
    });

    showComplianceStateChangeSuccessToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(complianceStateChangeSuccess),
            map(() => showToasterMessage({ message: 'Successfully updated clearance status' }))
        );
    });

    private _addMasterFileToContracts(contracts: Contract[]): Contract[] {
        contracts.unshift({ id: null, facilityName: 'Master File' });
        return contracts;
    }
}
